import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { EditPanelProps } from "./types";
import colors from "../../colors/colors";
import { useDraggable } from "./hooks/useDraggable";
import { useEditPanelFields } from "./hooks/useEditPanelFields";
import { getEditPanelStyles } from "./styles";
import { ConfirmDialog } from "../ConfirmDialog";
import {
  getPortalContainer,
  getPortalDocument,
  getIsFrameMode,
} from "../../dev/ThemeManager/utils";

import { PanelHeader } from "./components/PanelHeader";
import { SearchBar } from "./components/SearchBar";
import { ResponsiveTabs } from "./components/ResponsiveTabs";
import { CategorySidebar } from "./components/CategorySidebar";
import { FieldsArea } from "./components/FieldsArea";

export const EditPanel: React.FC<EditPanelProps> = ({
  displayName,
  fields,
  values,
  initialValues,
  onChange,
  onClose,
  onReset,
  screenPosition,
  onSave,
  isSaving = false,
  savedKeys,
  studioLockedKeys,
  onResetField,
  themeColors = {},
  themeFonts = {},
  themeIcons = {},
}) => {
  const { dragOffset, isDragging, handleMouseDown } = useDraggable();
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [openColorPicker, setOpenColorPicker] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    activeTab,
    setActiveTab,
    hasResponsiveFields,
    searchQuery,
    setSearchQuery,
    activeCategory,
    setActiveCategory,
    categoryList,
    hasSidebar,
    hasSearchBar,
    displayedGroupedFields,
    totalDisplayedFields,
    isShowingAllCategories,
    searchMatchCounts,
    collapsedGroups,
    toggleGroupCollapse,
  } = useEditPanelFields(fields, values);

  const isDirty = useMemo(() => {
    if (!initialValues) return false;
    return JSON.stringify(values) !== JSON.stringify(initialValues);
  }, [values, initialValues]);

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleDiscard = () => {
    setShowConfirmDialog(false);
    if (onReset) {
      onReset();
    }
    onClose();
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  const handleSaveAndClose = async () => {
    if (onSave) {
      onSave();
    }
    setShowConfirmDialog(false);
  };

  // Click-away listener to close color picker popover
  useEffect(() => {
    if (!openColorPicker) return;

    const portalDoc = getPortalDocument();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".edit-panel-color-popover") &&
        !target.closest(".edit-panel-color-swatch")
      ) {
        setOpenColorPicker(null);
      }
    };

    portalDoc.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      portalDoc.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [openColorPicker]);

  // Inject styles into parent document when in frame mode
  useEffect(() => {
    if (!getIsFrameMode()) return;

    const parentDoc = getPortalDocument();
    const styleId = "edit-panel-styles";

    if (!parentDoc.getElementById(styleId)) {
      const style = parentDoc.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes edit-panel-slideIn {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `;
      parentDoc.head.appendChild(style);
    }

    return () => {
      const existingStyle = parentDoc.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const fieldItemProps = {
    values,
    onChange,
    savedKeys,
    studioLockedKeys,
    onResetField,
    themeColors,
    themeFonts,
    themeIcons,
    openColorPicker,
    setOpenColorPicker,
  };

  const panelContent = (
    <>
      <style>{getEditPanelStyles(isDragging)}</style>

      <div
        ref={panelRef}
        className="edit-panel-root"
        style={{
          position: "fixed",
          top: screenPosition.y + dragOffset.y,
          left: screenPosition.x + dragOffset.x,
          background: colors.glass_bg_strong,
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          border: `1px solid ${colors.border_subtle}`,
          borderRadius: "20px",
          boxShadow: `
            0 8px 40px ${colors.shadow_medium},
            0 20px 60px ${colors.shadow_soft},
            0 0 0 1px ${colors.border_glass}
          `,
          zIndex: 999999,
          width: hasSidebar ? "800px" : "620px",
          maxWidth: hasSidebar ? "800px" : "620px",
          maxHeight: "450px",
          display: "flex",
          flexDirection: "column",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          animation: "edit-panel-slideIn 0.2s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <PanelHeader
          displayName={displayName}
          isDirty={isDirty}
          isSaving={isSaving}
          onReset={onReset}
          onSave={onSave}
          onClose={handleClose}
          handleMouseDown={handleMouseDown}
        />

        {hasSearchBar && (
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchInputRef={searchInputRef}
          />
        )}

        {hasResponsiveFields && (
          <ResponsiveTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        )}

        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {hasSidebar && (
            <CategorySidebar
              categoryList={categoryList}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              searchMatchCounts={searchMatchCounts}
            />
          )}

          <FieldsArea
            displayedGroupedFields={displayedGroupedFields}
            totalDisplayedFields={totalDisplayedFields}
            isShowingAllCategories={isShowingAllCategories}
            collapsedGroups={collapsedGroups}
            toggleGroupCollapse={toggleGroupCollapse}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            fieldItemProps={fieldItemProps}
          />
        </div>
      </div>
    </>
  );

  return (
    <>
      {ReactDOM.createPortal(panelContent, getPortalContainer())}
      {showConfirmDialog && (
        <ConfirmDialog
          title="Unsaved Changes"
          message="You have unsaved changes. Would you like to save them before closing?"
          confirmLabel="Save"
          cancelLabel="Discard"
          onConfirm={handleSaveAndClose}
          onCancel={handleDiscard}
          onDismiss={handleCancel}
          isLoading={isSaving}
        />
      )}
    </>
  );
};
